<!-- src/components/LocationCard.vue -->
<template>
  <AssetCard
    :view-mode="viewMode"
    :empty-title="location ? null : 'Aucune localisation associée'"
    :empty-message="location ? null : 'Ajoutez une localisation pour documenter le lieu'"
    :show-edit-button="showEditButton"
    :show-delete-button="showDeleteButton"
    :created-at="location?.created_at ?? null"
    :memos-count="computedMemosCount"
    :material-entities-count="computedMaterialEntitiesCount"
    :table-grid-class="'flex items-center'"
    :is-owned="isOwned"
    :is-public="isPublic"
    :is-private="!isPublic"
    :is-confidential="location?.is_confidential || false"
    :show-ownership-badge="showOwnershipBadge"
    :user-id="userId"
    :can-edit="canEditLocation"
    :can-delete="canDeleteLocation"
    @click="handleClick"
    @edit="handleEdit"
    @delete="handleDelete"
  >
    <!-- ========== MODE DEFAULT ==================================== -->
    <template #default="{ classes }">
      <div v-if="showTopIcon" class="absolute top-3 right-3">
        <MapPin class="h-5 w-5 text-gray-600" />
      </div>
      <div v-if="location" class="flex items-center gap-2 mb-2">
        <!-- Affichage confidentiel -->
        <span v-if="location.is_confidential && !isOwned" :class="[classes.title, 'text-gray-500 italic']">
          Donnée confidentielle
        </span>
        <!-- Affichage normal -->
        <span v-else :class="classes.title">{{ location.site || 'Sans titre' }}</span>
        <a
          v-if="location.geoname_id && !(location.is_confidential && !isOwned)"
          :href="'https://www.geonames.org/' + location.geoname_id"
          target="_blank"
          :class="classes.link"
        >
          <LinkIcon :class="classes.linkIcon" />
        </a>
      </div>
    </template>

    <!-- ► Slot INFO : liste verticalisée rendue par AssetCard -->
    <template #info="{ classes }">
      <!-- Affichage confidentiel -->
      <div v-if="location?.is_confidential && !isOwned" :class="classes.iconWithText">
        <MapPinned :class="classes.icon" />
        <span class="text-gray-500 italic">Informations confidentielles</span>
      </div>
      
      <!-- Affichage normal -->
      <template v-else>
        <div v-if="location?.address" :class="classes.iconWithText">
          <MapPinned :class="classes.icon" />
          <span>{{ location.address }}</span>
        </div>

        <div v-if="location?.post_code || location?.place" :class="classes.iconWithText">
          <LocateIcon :class="classes.icon" />
          <span>{{ location.post_code }} {{ location.place }}</span>
        </div>

        <div v-if="location?.country" :class="classes.iconWithText">
          <GlobeIcon :class="classes.icon" />
          <span>{{ location.country }}</span>
        </div>

        <div
          v-if="location?.latitude && location.longitude"
          :class="classes.iconWithText"
        >
          <CompassIcon :class="classes.icon" />
          <span>{{ formatCoordinates(location.latitude, location.longitude) }}</span>
        </div>
      </template>
    </template>

    <!-- ========== MODE TABLE ====================================== -->
    <template #table-row="{ classes }">
      <div class="flex items-center gap-2 min-w-0 flex-[1.25]">
        <MapPin :class="classes.icon" />
        <!-- Affichage confidentiel -->
        <span v-if="location?.is_confidential && !isOwned" :class="[classes.title, 'truncate text-gray-500 italic']">
          Donnée confidentielle
        </span>
        <!-- Affichage normal -->
        <span v-else :class="[classes.title, 'truncate']">{{ location?.site || 'Sans titre' }}</span>
      </div>
      <div class="flex items-center text-sm text-gray-600 min-w-0 flex-1 px-3">
        <!-- Affichage confidentiel -->
        <span v-if="location?.is_confidential && !isOwned" class="truncate text-gray-500 italic">
          Informations confidentielles
        </span>
        <!-- Affichage normal -->
        <span v-else class="truncate">{{ location?.address || 'Non spécifié' }}</span>
      </div>
      <div class="flex items-center text-sm text-gray-600 min-w-0 flex-1 px-3">
        <!-- Affichage confidentiel -->
        <span v-if="location?.is_confidential && !isOwned" class="truncate text-gray-500 italic">
          —
        </span>
        <!-- Affichage normal -->
        <span v-else class="truncate">{{ (location?.post_code || '') + ' ' + (location?.place || '') || 'Non spécifié' }}</span>
      </div>
      <div class="flex items-center gap-3 text-sm text-gray-600 min-w-0 px-3">
        <div class="flex items-center gap-1">
          <ClipboardIcon :class="classes.footerIcon" />
          <span>{{ computedMemosCount }}</span>
        </div>
        <div class="flex items-center gap-1">
          <AmphoraIcon :class="classes.footerIcon" />
          <span>{{ computedMaterialEntitiesCount }}</span>
        </div>
      </div>
      <TableRowActions
        :classes="classes"
        :show-edit-button="showEditButton"
        :show-delete-button="showDeleteButton"
        :can-edit="canEditLocation"
        :can-delete="canDeleteLocation"
        @edit="handleEdit"
        @delete="handleDelete"
      />
    </template>

    <!-- ========== MODE MEMOS-TABLE ================================ -->
    <template #memos-table-icon>
      <MapPin class="h-5 w-5 text-gray-500 shrink-0" />
    </template>

    <template #memos-table-content="{ classes }">
      <!-- Affichage confidentiel -->
      <div v-if="location?.is_confidential && !isOwned" class="flex items-center gap-2">
        <h3 :class="[classes.title, 'text-gray-500 italic']">Donnée confidentielle</h3>
        <span class="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">Confidentiel</span>
      </div>
      
      <!-- Affichage normal -->
      <div v-else>
        <div class="flex items-center gap-2">
          <h3 :class="classes.title">{{ location?.site || 'Sans titre' }}</h3>
          <a
            v-if="location?.geoname_id"
            :href="'https://www.geonames.org/' + location.geoname_id"
            target="_blank"
            :class="classes.link"
          >
            <LinkIcon :class="classes.linkIcon" />
          </a>
        </div>

        <div class="text-xs text-gray-500">
          <span v-if="location?.address" class="truncate">{{ location.address }}</span>
          <span v-if="location?.country"> — {{ location.country }}</span>
        </div>

        <div
          v-if="location?.latitude && location.longitude"
          class="flex items-center gap-1 text-xs text-gray-500"
        >
          <LocateIcon :class="classes.icon" />
          <span>{{ formatCoordinates(location.latitude, location.longitude) }}</span>
        </div>
      </div>
    </template>
  </AssetCard>
</template>

<script lang="ts" setup>
import {
  Amphora as AmphoraIcon,
  Clipboard as ClipboardIcon,
  Compass as CompassIcon,
  Globe as GlobeIcon,
  Link as LinkIcon,
  Locate as LocateIcon,
  MapPin,
  MapPinned
} from 'lucide-vue-next';
import type { PropType } from 'vue';
import { computed } from 'vue';

import { useRouter } from 'vue-router';
import type { LocationWithMemos } from '../../api/types/entities/location/relations';
import { useAuthStore } from '../../stores/auth';
import { useBreadcrumbsStore } from '../../stores/breadcrumbs';

import AssetCard from '../UI/AssetCard.vue';
import TableRowActions from '../UI/TableRowActions.vue';

const props = defineProps({
  location: {
    type: Object as PropType<Partial<LocationWithMemos> | null>,
    default: null
  },
  showActions: {
    type: Boolean,
    default: true
  },
  showEditButton: {
    type: Boolean,
    default: true
  },
  showDeleteButton: {
    type: Boolean,
    default: true
  },
  showTopIcon: {
    type: Boolean,
    default: false
  },
  viewMode: {
    type: String as PropType<'default' | 'table' | 'memostable'>,
    default: 'default'
  },
  memosCount: {
    type: Number,
    default: undefined
  },
  materialEntitiesCount: {
    type: Number,
    default: undefined
  },
  // Nouvelles props pour les permissions et propriété
  isOwned: {
    type: Boolean,
    default: false
  },
  isPublic: {
    type: Boolean,
    default: false
  },
  showOwnershipBadge: {
    type: Boolean,
    default: true
  },
  userId: {
    type: String,
    default: undefined
  }
});

const emit = defineEmits(['edit', 'delete']);

const router = useRouter();
const breadcrumbsStore = useBreadcrumbsStore();
const authStore = useAuthStore();

/* ------------------------------------------------------------------ */
/* compteurs                                                          */
/* ------------------------------------------------------------------ */
const computedMemosCount = computed(() => {
  if (props.memosCount !== undefined) return props.memosCount;
  if (!props.location || !('memos' in props.location)) return 0;
  return (props.location as LocationWithMemos).memos?.length || 0;
});

const computedMaterialEntitiesCount = computed(() => {
  if (props.materialEntitiesCount !== undefined) return props.materialEntitiesCount;
  if (!props.location || !('material_entities' in props.location)) return 0;
  return (props.location as LocationWithMemos).material_entities?.length || 0;
});

/* ------------------------------------------------------------------ */
/* permissions                                                        */
/* ------------------------------------------------------------------ */
const canEditLocation = computed((): boolean => {
  return props.location && props.location.user_id ? authStore.canEditAsset(props.location as any) : false;
});

const canDeleteLocation = computed((): boolean => {
  return props.location && props.location.user_id ? authStore.canDeleteAsset(props.location as any) : false;
});

/* ------------------------------------------------------------------ */
/* helpers                                                            */
/* ------------------------------------------------------------------ */
const formatCoordinates = (lat: number, lng: number): string =>
  `${lat.toFixed(3)}°, ${lng.toFixed(3)}°`;

/* ------------------------------------------------------------------ */
/* navigation                                                         */
/* ------------------------------------------------------------------ */
const handleClick = () : void => {
  if (!props.location) return;

  // Empêcher la navigation si la location est confidentielle et que l'utilisateur n'est pas propriétaire
  if (props.location.is_confidential && !props.isOwned) {
    return;
  }

  // breadcrumb
  const locationName = props.location.site || props.location.place || 'Lieu';
  breadcrumbsStore.updateBreadcrumbs([
    { name: locationName, path: `/location/${props.location.uuid}` }
  ]);

  router.push(`/location/${props.location.uuid}`);
};

const handleEdit = () : void => {
  emit('edit', props.location);
};

const handleDelete = () : void  => {
  emit('delete', props.location);
};
</script>